import { makeAutoObservable, reaction } from 'mobx';
import i18next from 'i18next';

import { analytics } from '@/app/parts/Analytics';

import { betStatus, errorMap } from '@/lib/statuses';
import { getFormattedCurrency } from '@/helpers/getFormattedCurrency';
import { oddsConverter } from '@/helpers/oddsConverter';
import { expressMaxOdd } from '@/config/constants';

class BetslipErrors {
  constructor(model) {
    makeAutoObservable(this);

    this.root = model;

    this.reactionWithDebounce({
      delay: 100,
      effect: () => {
        if (this.isRemovingAfterPlaceBet) {
          this.isRemovingAfterPlaceBet = false;
        }
      },
      expression: () => this.isRemovingAfterPlaceBet,
    });

    this.reactionWithDebounce({
      delay: 100,
      effect: () => {
        if (
          this.root.betslip.currentTab !== 'SINGLEBET' &&
          !this.root.betslip.inProgress &&
          !this.isMaxBetError
        ) {
          this.resetBetslipNotification();
        }
      },
      expression: () => this.root.betslip.expressAmount,
    });

    reaction(
      () => [this.root.betslip.bets.length, this.root.betslip.currentTab],
      () => {
        if (!this.isRemovingAfterPlaceBet && !this.maxOddsReach && !this.root.betslip.inProgress) {
          this.resetBetslipNotification();
        }
      },
    );

    reaction(
      () => this.root.betslip.acceptChangesForBets,
      () => {
        if (!this.root.betslip.inProgress) {
          this.resetBetslipNotification();
        }
      },
    );

    reaction(
      () => this.root.betslip.inProgress,
      (inProgress) => {
        if (inProgress && !this.isMaxBetError) {
          this.resetBetslipNotification();
        } else {
          this.handleBetSlipNotifications();
        }
      },
    );

    reaction(
      () => [
        this.root.betslip.expressOdd,
        this.root.ui.oddsFormat,
        this.root.betslip.currentTab,
        this.root.betslip.bets.length,
      ],
      (val) => {
        const [expressOdd, oddsFormat, currentTab] = val;
        const odds = oddsConverter(expressOdd, oddsFormat);
        if (expressOdd >= expressMaxOdd && currentTab === 'EXPRESS') {
          this.handleErrorCode({ errorCode: 'maxOddsReach', errorStatus: { odds } });
        } else if (this.maxOddsReach && !this.root.betslip.inProgress) {
          this.resetBetslipNotification();
        }
      },
    );
  }

  betslipNotification = '';

  maxOddsReach = false;

  isRemovingAfterPlaceBet = false;

  errorBetStatuses = [];

  isMaxBetError = false;

  setErrorBetStatuses = (val = null) => {
    if (val) {
      this.errorBetStatuses.push(val);
    } else {
      this.errorBetStatuses = [];
    }
  };

  setBetslipNotification = (message = '') => {
    this.betslipNotification = message;
  };

  resetBetslipNotification = () => {
    this.setBetslipNotification();
    this.root.betslip.bets.forEach((bet) => {
      if (bet.currentError !== 'maxBetError' && String(bet.currentError) !== '503') {
        bet.updateProp('currentError', null);
      }
    });
    this.maxOddsReach = false;
    this.isMaxBetError = false;
  };

  handleSingleBetError = ({ errorCode, errorStatus }) => {
    const betsWithErrors = this.root.betslip.bets.filter((bet) => errorStatus.ids.includes(bet.id));
    if (betsWithErrors.length) {
      betsWithErrors.forEach((bet) => {
        const currentErrorData = errorStatus.ticketId
          ? `${errorCode};${errorStatus.ticketId}`
          : `${errorCode}`;
        bet.updateProp('currentError', currentErrorData);

        if (errorCode === 35) {
          bet.updateProp('minStake', errorStatus.minBet ?? '');
        }
      });
    } else {
      // 'Something went wrong. Please try again later or contact support'
      this.setBetslipNotification(i18next.t(betStatus.getPlaceBetStatus('defaultError')));
    }
  };

  handleExpressError = ({ errorCode, errorStatus }) => {
    const { ticketId = '' } = errorStatus ?? { minBet: null, ticketId: '' };
    const betId = ticketId ? `; BetId: ${ticketId}` : '';
    let errorMessage = '';
    console.log(errorCode, errorStatus);
    switch (true) {
      case errorCode === 'maxOddsReach':
        this.maxOddsReach = true;
        // 'Max odds reached'
        errorMessage = errorMap[errorCode] || '';
        analytics?.pushErrorNotification(`Error: ${errorMessage}${betId}`);
        return `${i18next.t(errorMessage)}: ${errorStatus.odds || '-'}`;

      case !!errorMap[errorCode]:
        if (errorCode === 'invalidAmount') {
          analytics?.pushErrorNotification(`Error: Empty amount${betId}`);
        }
        errorMessage = errorMap[errorCode];
        analytics?.pushErrorNotification(`Error: ${errorMessage}${betId}`);

        return i18next.t(errorMessage, {
          interpolation: { escapeValue: false },
          minBet: `${getFormattedCurrency(this.root.profile.currency?.sign)} ${
            errorStatus.minBet ?? ''
          }`,
        });

      default:
        // 'Something went wrong. Please try again later or contact support'
        errorMessage = betStatus.getPlaceBetStatus(errorCode);
        analytics?.pushErrorNotification(`Error: ${errorMessage}${betId}`);
        return i18next.t(errorMessage);
    }
  };

  handleErrorCode = ({ errorCode, errorStatus }) => {
    const { currentTab = '' } = this.root.betslip;
    const isExpress = currentTab === 'EXPRESS';
    const isSinglebet = currentTab === 'SINGLEBET';

    if (isSinglebet) {
      this.handleSingleBetError({
        errorCode,
        errorStatus,
      });
    }

    if (isExpress) {
      const errorText = this.handleExpressError({
        errorCode,
        errorStatus,
      });

      this.setBetslipNotification(errorText);
    }
  };

  handleBetSlipNotifications = () => {
    if (this.root.betslip.inProgress) {
      return;
    }

    this.errorBetStatuses.forEach((errorStatus) => {
      const { errorCode = 'defaultError' } = errorStatus;
      this.isMaxBetError =
        errorCode === 'maxBetError' || (!this.root.betslip.betItemWithMaxBet && errorCode === 5);
      this.handleErrorCode({
        errorCode: this.isMaxBetError ? 'maxBetError' : errorCode,
        errorStatus,
      });
    });

    this.setErrorBetStatuses();
  };

  reactionWithDebounce({ expression, effect, delay }) {
    let timeout;

    reaction(expression, (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        effect(...args);
      }, delay);
    });

    return () => {
      clearTimeout(timeout);
    };
  }
}

export { BetslipErrors };
